import { useEffect, useMemo, useState } from 'react';
import { db } from '../../lib/firebase';
import { removeDoc, saveDoc } from '../../lib/firestore-write';
import type { ChapterDoc, ResourceDoc, SectionDoc } from '../../lib/data-model';
import { ConfirmDialog, EmptyState, Loading, toast } from '../ui';
import { kindBadge } from '../../pages/public-helpers/misc';
import { errOf, useSaver } from './ListAdmin';
import { ResourceForm } from './FormEditor';

type Ch = ChapterDoc & { id: string };
type Sec = SectionDoc & { id: string };
type Res = ResourceDoc & { id: string };

const sortOrder = <T extends { order: number }>(arr: T[]) => [...arr].sort((a, b) => a.order - b.order);
const nextOrder = <T extends { order: number }>(arr: T[]) => (arr.length ? Math.max(...arr.map(d => d.order)) + 1 : 0);

/**
 * Full-page (in-tab) chapter content editor: chapter meta, inline section
 * cards with save-on-blur, and inline resource forms — no popup chains.
 */
export function ChapterEditor({
  courseId,
  courseName,
  chapterId,
  loading,
  chapters,
  sections,
  resources,
  onBack,
  onCreated
}: {
  courseId: string;
  courseName: string;
  chapterId: string | null;
  loading: boolean;
  chapters: Ch[];
  sections: Sec[];
  resources: Res[];
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const isNew = chapterId === null;
  const existing = chapterId ? chapters.find(c => c.id === chapterId) : undefined;
  const id = useMemo(
    () => existing?.id ?? (chapterId ? chapterId : `${courseId}-ch${Date.now().toString(36)}`),
    [existing, chapterId, courseId]
  );

  const init = {
    num: existing?.num ?? String(chapters.length + 1),
    title: existing?.title ?? '',
    blurb: existing?.blurb ?? '',
    hidden: existing?.hidden ?? false
  };
  const [draft, setDraft] = useState(init);
  const [saved, setSaved] = useState(init);

  const [form, setForm] = useState<{ sectionId: string; id?: string } | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<{ kind: 'section' | 'resource' | 'chapter'; id: string; name: string } | null>(null);

  const secs = useMemo(
    () => sortOrder(sections.filter(s => s.chapterId === (existing?.id ?? chapterId ?? ' '))),
    [sections, existing, chapterId]
  );
  const resourcesOf = (sid: string) => sortOrder(resources.filter(r => r.sectionId === sid));

  const dirty = draft.num !== saved.num || draft.title !== saved.title || draft.blurb !== saved.blurb || draft.hidden !== saved.hidden;

  const { errors, saving, save } = useSaver(
    'chapters',
    id,
    () => ({
      courseId,
      order: existing?.order ?? nextOrder(chapters),
      num: draft.num,
      title: draft.title.trim() || (existing?.title ?? ''),
      blurb: draft.blurb,
      hidden: draft.hidden
    }),
    () => {
      setSaved({ ...draft });
      if (!existing) onCreated(id);
    }
  );

  const set = <K extends keyof typeof init>(k: K, v: (typeof init)[K]) => setDraft(d => ({ ...d, [k]: v }));

  const toggleSecCollapsed = (sid: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });

  const toggleSectionHidden = (s: Sec) =>
    void saveDoc(db, 'sections', s.id, {
      chapterId: s.chapterId,
      order: s.order,
      title: s.title,
      summary: s.summary ?? '',
      hidden: !s.hidden
    })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const toggleResourceHidden = (r: Res) =>
    void saveDoc(db, 'resources', r.id, { ...r, hidden: !r.hidden })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const addSection = async (t: string, summary: string): Promise<boolean> => {
    if (!existing) return false;
    try {
      await saveDoc(db, 'sections', `${existing.id}-s${Date.now().toString(36)}`, {
        chapterId: existing.id,
        order: nextOrder(secs),
        title: t,
        summary,
        hidden: false
      });
      toast('Section added', 'ok');
      return true;
    } catch (e) {
      toast(errOf(e), 'err');
      return false;
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      if (deleting.kind === 'chapter') {
        for (const s of secs) {
          for (const r of resourcesOf(s.id)) await removeDoc(db, 'resources', r.id);
          await removeDoc(db, 'sections', s.id);
        }
        await removeDoc(db, 'chapters', deleting.id);
        toast('Chapter deleted', 'ok');
        onBack();
      } else if (deleting.kind === 'section') {
        for (const r of resourcesOf(deleting.id)) await removeDoc(db, 'resources', r.id);
        await removeDoc(db, 'sections', deleting.id);
        toast('Section deleted', 'ok');
        if (form && form.sectionId === deleting.id) setForm(null);
      } else {
        await removeDoc(db, 'resources', deleting.id);
        toast('Resource deleted', 'ok');
      }
    } catch (e) {
      toast(errOf(e), 'err');
    }
  };

  if (chapterId && loading && !existing) return <Loading label="Loading chapter…" />;

  return (
    <div className="admin-wrap">
      <button className="ed-back" onClick={onBack}>← Content tree · {courseName}</button>

      <section className="ed-hero">
        <div className="ed-hero-top">
          <span className="num-pill">CHAPTER {draft.num.trim() || '?'}</span>
          <span className="grow" />
          <button className={`row-btn${draft.hidden ? ' on' : ''}`} onClick={() => set('hidden', !draft.hidden)} aria-pressed={draft.hidden} title={draft.hidden ? 'Visible to everyone once saved — click to hide' : 'Hidden — click to make visible'}>
            {draft.hidden ? 'Hidden' : 'Visible'}
          </button>
        </div>
        <input
          className="ed-title"
          placeholder="Chapter title"
          value={draft.title}
          onChange={e => set('title', e.target.value)}
        />
        {errors.title ? <div className="error">{errors.title}</div> : null}
        <textarea
          className="ed-blurb"
          placeholder="Short blurb shown under the chapter title…"
          value={draft.blurb}
          onChange={e => set('blurb', e.target.value)}
        />
        <div className="ed-fields">
          <div className="field" style={{ margin: 0, maxWidth: 160 }}>
            <label>Number</label>
            <input type="text" value={draft.num} onChange={e => set('num', e.target.value)} />
            {errors.num ? <div className="error">{errors.num}</div> : null}
          </div>
        </div>
      </section>

      {isNew ? (
        <div className="state-block">
          <div className="glyph" aria-hidden>✦</div>
          <h2>Set the basics, then save</h2>
          <p>Give the chapter a title and press “Save chapter”. Sections and their resources appear here.</p>
        </div>
      ) : (
        <>
          <div className="admin-bar">
            <div className="admin-bar-left">
              <span className="mono" style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--ink3)' }}>
                {secs.length} SECTION{secs.length === 1 ? '' : 'S'} · {secs.reduce((n, s) => n + resourcesOf(s.id).length, 0)} FILES
              </span>
            </div>
          </div>

          {secs.length === 0 ? (
            <EmptyState glyph="◈" title="No sections yet" body="Add the first section below — you can drop videos, PDFs and links into it." />
          ) : (
            <div className="sec-acc">
              {secs.map((s, i) => (
                <SectionCard
                  key={s.id}
                  sec={s}
                  index={i}
                  resList={resourcesOf(s.id)}
                  collapsed={collapsed.has(s.id)}
                  courseId={courseId}
                  form={form && form.sectionId === s.id ? form : null}
                  onToggle={() => toggleSecCollapsed(s.id)}
                  onHidden={() => toggleSectionHidden(s)}
                  onDelete={() => setDeleting({ kind: 'section', id: s.id, name: s.title })}
                  onAddForm={() => setForm({ sectionId: s.id })}
                  onEditForm={rid => setForm({ sectionId: s.id, id: rid })}
                  onCloseForm={() => setForm(null)}
                  onResourceHidden={toggleResourceHidden}
                  onResourceDelete={r => setDeleting({ kind: 'resource', id: r.id, name: r.title })}
                />
              ))}
            </div>
          )}

          <AddSectionCard onAdd={addSection} />
        </>
      )}

      <div className="save-bar">
        <span className="note">{dirty ? 'Unsaved chapter changes.' : 'All chapter changes saved.'}</span>
        {existing && (
          <button className="btn btn-danger btn-sm" onClick={() => setDeleting({ kind: 'chapter', id: existing.id, name: existing.title })}>
            Delete chapter
          </button>
        )}
        <button className="btn btn-primary" onClick={() => void save()} disabled={saving || (!dirty && !isNew)}>
          {saving ? 'Saving…' : isNew ? 'Save chapter' : 'Save chapter'}
        </button>
      </div>

      {deleting?.kind === 'chapter' && existing && (
        <ConfirmDialog
          title="Delete chapter?"
          confirmLabel="Delete permanently"
          body={
            <p>
              <strong>{existing.title}</strong> — {secs.length} section{secs.length === 1 ? '' : 's'} and{' '}
              {secs.reduce((n, s) => n + resourcesOf(s.id).length, 0)} resource
              {secs.reduce((n, s) => n + resourcesOf(s.id).length, 0) === 1 ? '' : 's'} will be permanently deleted.
            </p>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
      {deleting?.kind === 'section' && (
        <ConfirmDialog
          title="Delete section?"
          confirmLabel="Delete permanently"
          body={
            <p>
              <strong>{deleting.name}</strong> and its resources are deleted too. This cannot be undone.
            </p>
          }
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
      {deleting?.kind === 'resource' && (
        <ConfirmDialog
          title="Delete resource?"
          confirmLabel="Delete permanently"
          body={<p><strong>{deleting.name}</strong> — this cannot be undone.</p>}
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function SectionCard({
  sec,
  index,
  resList,
  collapsed,
  courseId,
  form,
  onToggle,
  onHidden,
  onDelete,
  onAddForm,
  onEditForm,
  onCloseForm,
  onResourceHidden,
  onResourceDelete
}: {
  sec: Sec;
  index: number;
  resList: Res[];
  collapsed: boolean;
  courseId: string;
  form: { id?: string } | null;
  onToggle: () => void;
  onHidden: () => void;
  onDelete: () => void;
  onAddForm: () => void;
  onEditForm: (id: string) => void;
  onCloseForm: () => void;
  onResourceHidden: (r: Res) => void;
  onResourceDelete: (r: Res) => void;
}) {
  const [title, setTitle] = useState(sec.title);
  const [summary, setSummary] = useState(sec.summary ?? '');

  // adopt remote changes (e.g. the blur-save round-trip) when not mid-edit
  useEffect(() => {
    setTitle(sec.title);
    setSummary(sec.summary ?? '');
  }, [sec.title, sec.summary]);

  const commit = () => {
    const t = title.trim();
    if (!t) {
      setTitle(sec.title);
      toast('Section needs a title', 'err');
      return;
    }
    if (t === sec.title && summary === (sec.summary ?? '')) return;
    void saveDoc(db, 'sections', sec.id, {
      chapterId: sec.chapterId,
      order: sec.order,
      title: t,
      summary,
      hidden: !!sec.hidden
    })
      .then(() => toast('Section saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));
  };

  const formExisting = form?.id ? resList.find(r => r.id === form.id) : undefined;

  return (
    <div className={`sec-card${sec.hidden ? ' off' : ''}`}>
      <div className="sec-card-head">
        <button className="row-btn" onClick={onToggle} title={collapsed ? 'Expand section' : 'Collapse section'}>
          {collapsed ? '▸' : '▾'}
        </button>
        <span className="sec-num">SEC {index + 1}</span>
        <input
          className="sec-input"
          value={title}
          placeholder="Section title"
          onChange={e => setTitle(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
        />
        <span className={`pill ${resList.length ? 'ok' : 'line'}`}>{resList.length} files</span>
        <button className={`row-btn${sec.hidden ? ' on' : ''}`} onClick={onHidden} title={sec.hidden ? 'Make visible' : 'Hide from site'}>
          {sec.hidden ? 'Show' : 'Hide'}
        </button>
        <button className="row-btn danger" onClick={onDelete}>Delete</button>
        <textarea
          className="sec-text"
          value={summary}
          placeholder="One-line summary shown under the section…"
          onChange={e => setSummary(e.target.value)}
          onBlur={commit}
        />
      </div>

      {!collapsed && (
        <div className="sec-body">
          <div className="res-list">
            {resList.map(r => {
              const b = kindBadge(r.kind);
              return (
                <div key={r.id} className={`res-a-row${r.hidden ? ' off' : ''}`}>
                  <span className="res-badge" style={{ background: b.bg, color: b.fg }} aria-hidden>
                    {b.glyph}
                  </span>
                  <span className="res-a-meta">
                    <span className="t">{r.title}</span>
                    <span className="m">{(r.meta ?? '').trim() || r.kind.toUpperCase()}</span>
                  </span>
                  <span className="res-a-acts">
                    <button className={`row-btn${r.hidden ? ' on' : ''}`} onClick={() => onResourceHidden(r)}>
                      {r.hidden ? 'Show' : 'Hide'}
                    </button>
                    <button className="row-btn" onClick={() => onEditForm(r.id)}>Edit</button>
                    <button className="row-btn danger" onClick={() => onResourceDelete(r)}>Delete</button>
                  </span>
                </div>
              );
            })}
            {resList.length === 0 && <p className="muted" style={{ margin: '4px 2px 8px' }}>No resources yet — add one below.</p>}
          </div>

          {form ? (
            <ResourceForm
              sectionId={sec.id}
              courseId={courseId}
              order={formExisting ? formExisting.order : nextOrder(resList)}
              existing={formExisting}
              onDone={onCloseForm}
              onCancel={onCloseForm}
            />
          ) : (
            <button className="add-res" onClick={onAddForm}>+ New resource</button>
          )}
        </div>
      )}
    </div>
  );
}

function AddSectionCard({ onAdd }: { onAdd: (title: string, summary: string) => Promise<boolean> }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button className="add-sec" onClick={() => setOpen(true)}>
        + New section
      </button>
    );
  }

  const submit = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    const ok = await onAdd(title.trim(), summary);
    setBusy(false);
    if (ok) {
      setTitle('');
      setSummary('');
      setOpen(false);
    }
  };

  return (
    <div className="add-sec open">
      <input className="sec-input" placeholder="New section title" value={title} onChange={e => setTitle(e.target.value)} autoFocus />
      <input className="sec-text" style={{ flex: '1 1 220px' }} placeholder="Summary (optional)" value={summary} onChange={e => setSummary(e.target.value)} />
      <button className="btn btn-primary btn-sm" onClick={() => void submit()} disabled={busy || !title.trim()}>
        {busy ? 'Adding…' : 'Add section'}
      </button>
      <button className="btn btn-outline btn-sm" onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}
